import * as stylex from '@stylexjs/stylex'
import { type ElementType, type ReactNode } from 'react'
import { Box, type BoxProps } from '../Box/Box'
import { Container } from '../Container/Container'
import { type MobileMediaQuery } from '../tokens/breakpoints.stylex'
import { colors } from '../tokens/colors.stylex'
import { zIndex } from '../tokens/zIndex.stylex'
import { VStack } from '../VStack/VStack'

const MOBILE: MobileMediaQuery = '@media (max-width: 480px)'

const baseStyles = stylex.create({
  root: {
    pointerEvents: 'none',
    position: 'fixed',
    zIndex: zIndex.appBar,
    bottom: 0,
    left: 0,
    right: 0,
  },
  container: {
    padding: {
      [MOBILE]: 0,
      default: '0 12px 0 12px',
    },
  },
  safeAreaBox: {
    backgroundColor: colors.white,
    height: 'env(safe-area-inset-bottom, 0)',
    width: '100%',
  },
})

export type FooterStackProps<RootComponent extends ElementType = 'footer', BoxElement extends HTMLElement = HTMLElement> = {
  /** 是否啟用 Container 包裹（限制最大寬度） */
  enableContainer?: boolean
  /** 渲染透明頁尾內容的函式 */
  renderTransparentFooters?: () => ReactNode
} & BoxProps<RootComponent, BoxElement>

/**
 * FooterStack 是固定在頁面底部的頁尾堆疊容器。
 * 支援透明頁尾與一般頁尾的分層排列，並處理 safe-area-inset-bottom。
 */
export const FooterStack = <RootComponent extends ElementType = 'footer', BoxElement extends HTMLElement = HTMLElement>(props: FooterStackProps<RootComponent, BoxElement>) => {
  const {
    component = 'footer',
    enableContainer,
    rootStyle,
    renderTransparentFooters,
    children,
    ...rest
  } = props

  const renderContent = () => {
    if (renderTransparentFooters) {
      return (
        <VStack pointerEvents="none">
          <VStack>
            {renderTransparentFooters()}
          </VStack>
          <VStack pointerEvents="auto">
            {children}
            <div {...stylex.props(baseStyles.safeAreaBox)} />
          </VStack>
        </VStack>
      )
    }
    return (
      <VStack pointerEvents="auto">
        {children}
        <div {...stylex.props(baseStyles.safeAreaBox)} />
      </VStack>
    )
  }

  const renderContentWithContainer = () => {
    if (enableContainer) {
      return (
        <Container rootStyle={[baseStyles.container, rootStyle]}>
          {renderContent()}
        </Container>
      )
    }
    return renderContent()
  }

  return (
    <Box component={component} rootStyle={[baseStyles.root, rootStyle]} {...rest}>
      {renderContentWithContainer()}
    </Box>
  )
}
